import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import { clearOrderDetail } from '../../redux/slices/orders'
import useGetOrderDetail from './useGetOrderDetail'
import useOrderConfirmationConfigGuards from './useOrderConfirmationConfigGuards'
import useOrderDistributor from './useOrderDistributor'
import type { IContainers } from '../../interfaces/config'

const FUNCTION_NAME = 'useOrderDetail'

/**
 * Everything the Order Detail screen does that is not rendering.
 *
 * The template is a `Default` delegate like every other page here, so this is
 * where the screen's behaviour lives: read the id off the route, fetch,
 * resolve the supplying depot's name, and report a misconfigured
 * `orderConfirmation` block. The panel's own props are injected at
 * prop-resolution time (`getOrderDetailPanelProps`), not returned from here,
 * because the component tree is authored in SCS config and this hook has
 * nothing to hand it to.
 *
 * Being the screen's single behaviour owner is what makes it the right place
 * to mount the hooks below: each of them is written on the assumption that one
 * instance exists per screen, and this is the only caller — `OrderDetail.tsx`
 * is the only caller of *this*.
 *
 * `container` is the screen's authored config, passed straight through from
 * the template. Nothing here reads it; it exists so `useOrderDistributor` can
 * see whether the panel switches its row off before paying for a lookup.
 * Optional so a caller that has no container — every test that drives this
 * hook directly — behaves as an unconfigured storefront does, which is the
 * permissive direction.
 */
export const useOrderDetail = (container?: IContainers) => {
  const { orderId = '' } = useParams<{ orderId: string }>()
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const dispatch = useAppDispatch()
  const { fetchOrderDetail } = useGetOrderDetail()

  useOrderConfirmationConfigGuards(FUNCTION_NAME)
  /*
   * Resolves `selected.storeCode` to the depot name the panel's distributor
   * row shows (COM-889). It watches the slice rather than taking the order as
   * an argument, so it is a mount and not a call: the order it depends on has
   * not been fetched yet at this point, and on a poll it changes repeatedly
   * afterwards. The container it *does* take gates the lookup — see
   * `isDistributorSuppressed`.
   */
  useOrderDistributor(container)

  /**
   * Fetch on mount, and again if the route's id changes under a mounted
   * screen.
   *
   * `sessionId` is a dependency and a guard, not merely a value read. It is
   * dispatched from an effect in `Config`, and effects run children-first, so
   * on the commit that first renders this screen it is still `undefined` here.
   * Fetching then would hit `fetchOrderDetail`'s session guard and paint
   * "your session has ended" over an order that is about to load perfectly
   * well.
   *
   * `undefined` is therefore read as "not known yet" and skipped; `''` is read
   * as a session that exists and is unusable, and is passed through to the
   * guard, which is what makes the `sessionEnded` state reachable at all.
   */
  useEffect(() => {
    if (sessionId === undefined) return
    fetchOrderDetail(orderId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, sessionId])

  /**
   * Leaving the screen resets the detail half of the slice (COM-1190).
   *
   * Here rather than in `backToHistory`, because this runs on every way out:
   * the in-app back on either of its branches, and the device's or browser's
   * own back, which never reaches that handler and used to leave `selected`
   * set. And it runs when the panel is already gone, so no frame paints it
   * emptied, however the router schedules its update.
   *
   * Its only dep is `dispatch`, which is the Provider store's own and stable
   * for the store's lifetime, so only a true unmount runs this. A changed
   * `orderId` under the mounted screen re-fetches through the effect above and
   * resets nothing, and the refresh nonce never remounts anything. That holds under the legacy root
   * `index.tsx` mounts with; a `createRoot` + StrictMode migration would also
   * run this cleanup on the simulated unmount between the two mounts.
   *
   * Here rather than in `useGetOrderDetail`'s own cleanup, defensively: no
   * second instance of that hook exists in production today, but if one did,
   * only the screen's may reset the slice. Both cleanups run in the same
   * passive flush, so an in-flight tick resolves either before both (and this
   * clear overwrites what it wrote) or after both (and the token bumped by
   * `useGetOrderDetail`'s cleanup discards it). Their order does not matter.
   */
  useEffect(
    () => () => {
      dispatch(clearOrderDetail())
    },
    [dispatch],
  )
}

export default useOrderDetail
